import cn from 'classnames';
import React from 'react';

import { useSelector } from 'react-redux';
import FillAspectRatio from 'components/FillAspectRatio';
import AdvancedEditorPreview, {
  AdvancedEditorPreviewProps,
} from 'containers/AdvancedEditorPreview';
import useOnMount from 'hooks/useOnMount';
import {
  aspectRatioSelector,
  embedUrlSelector,
} from 'redux/modules/embed/selectors';
import { getAspectRatioName } from 'utils/aspect-ratio';
import EmptyPreview from './EmptyPreview';
import { block } from './utils';

type PreviewProps = Pick<AdvancedEditorPreviewProps, 'playing' | 'volume'>;

export interface PreviewPaneProps extends PreviewProps {
  className?: string;
  onPlayerDestroy?: AdvancedEditorPreviewProps['onDestroy'];
  onPlayerPlay?: AdvancedEditorPreviewProps['onPlay'];
  onPlayerReady?: () => void;
  onPlayerPause?: AdvancedEditorPreviewProps['onPause'];
  renderDragFixOverlay?: AdvancedEditorPreviewProps['dragFixOverlay'];
}

const PreviewPane: React.FC<PreviewPaneProps> = ({
  className,
  onPlayerDestroy,
  onPlayerReady,
  onPlayerPlay,
  onPlayerPause,
  playing,
  renderDragFixOverlay,
  volume,
}) => {
  const aspectRatio = useSelector(aspectRatioSelector);
  const embedUrl = useSelector(embedUrlSelector);
  const isEmpty = !embedUrl;
  const ratioName = getAspectRatioName(aspectRatio);

  useOnMount(() => {
    /*
     * if there's no embedUrl, we won't render the EmbedPlayer, so EmbedPlayer's onReady will never
     * fire.  fire it here in case anyone else is waiting for it.
     */
    if (!embedUrl) {
      onPlayerReady?.();
    }
  });

  return (
    <div
      className={cn(
        block({
          default: !isEmpty,
          empty: isEmpty,
          [ratioName]: !!ratioName,
        }),
        className,
      )}
    >
      {isEmpty ? (
        <EmptyPreview />
      ) : (
        <FillAspectRatio
          aspectRatio={aspectRatio}
          className={block('preview-wrapper')}
          element={
            <AdvancedEditorPreview
              className="preview-pane__preview"
              dragFixOverlay={renderDragFixOverlay}
              onPlay={onPlayerPlay}
              onPause={onPlayerPause}
              onReady={onPlayerReady}
              onDestroy={onPlayerDestroy}
              playing={playing}
              volume={volume}
            />
          }
        />
      )}
    </div>
  );
};

export default PreviewPane;
